/**
 * FetchPass.ts — compute pass that builds a 2D wind-fetch texture.
 *
 * Output: r32float texture, sampled by the water shader to attenuate wave
 * displacement (and amplify shoreline foam) on the lee side of land.
 *
 * Re-runs only when wind direction or water level changes (dirty flag).
 */

import fetchWgsl from "../shaders/fetch.wgsl?raw";
import type { TerrainRenderPass } from "./TerrainRenderPass";
import { createUniformBuffer } from "../gpu/Buffers";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";

const STRUCT_SIZE = 32; // 8 × f32 (16-byte aligned)

export interface FetchPassOptions {
  /** Output resolution. Default 256. */
  resolution?: number;
  /** Maximum march distance. Default 1500 m. */
  maxFetch?: number;
  /** Number of march steps. Default 64. */
  steps?: number;
}

export class FetchPass implements RenderPass {
  readonly name = "FetchPass";

  readonly resolution: number;
  readonly maxFetch: number;
  readonly steps: number;
  readonly extent: number;

  private device: GPUDevice;
  private terrainPass: TerrainRenderPass;
  private pipeline: GPUComputePipeline;
  private bindGroup: GPUBindGroup;
  private uniform: GPUBuffer;

  private texture: GPUTexture;
  /** Sampled view (used by water render pass). */
  readonly view: GPUTextureView;

  private lastWindDirDeg = NaN;
  private lastWaterLevel = NaN;

  constructor(
    device: GPUDevice,
    terrainPass: TerrainRenderPass,
    opts: FetchPassOptions = {},
  ) {
    this.device = device;
    this.terrainPass = terrainPass;
    this.resolution = opts.resolution ?? 256;
    this.maxFetch = opts.maxFetch ?? 1500;
    this.steps = opts.steps ?? 64;
    this.extent = terrainPass.heightmap.extent;

    this.texture = device.createTexture({
      label: "FetchPass.fetch",
      size: { width: this.resolution, height: this.resolution },
      format: "r32float",
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.view = this.texture.createView({ label: "FetchPass.fetch.view" });

    this.uniform = createUniformBuffer(device, STRUCT_SIZE, "FetchPass.params");

    const module = device.createShaderModule({
      label: "fetch.wgsl",
      code: fetchWgsl,
    });
    const bgl = device.createBindGroupLayout({
      label: "FetchPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: { access: "write-only", format: "r32float" },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "FetchPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      compute: { module, entryPoint: "cs_main" },
    });
    this.bindGroup = device.createBindGroup({
      label: "FetchPass.bg",
      layout: bgl,
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: terrainPass.heightmapView() },
        { binding: 2, resource: this.view },
      ],
    });
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const { state } = frame;
    const dirty =
      state.windDirectionDeg !== this.lastWindDirDeg ||
      this.terrainPass.waterLevel !== this.lastWaterLevel;
    if (!dirty) return;
    this.lastWindDirDeg = state.windDirectionDeg;
    this.lastWaterLevel = this.terrainPass.waterLevel;

    const a = (state.windDirectionDeg * Math.PI) / 180;
    // Wind blows TOWARD this direction (matches OceanSimulation convention).
    const wx = -Math.sin(a);
    const wz = Math.cos(a);
    const data = new Float32Array([
      this.extent,
      this.resolution,
      wx,
      wz,
      this.terrainPass.waterLevel,
      this.maxFetch,
      this.steps,
      0,
    ]);
    this.device.queue.writeBuffer(this.uniform, 0, data);

    const pass = encoder.beginComputePass({ label: "FetchPass" });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    const wg = Math.ceil(this.resolution / 8);
    pass.dispatchWorkgroups(wg, wg, 1);
    pass.end();
  }

  destroy(): void {
    this.texture.destroy();
  }
}
